<template>
  <div class="heatmap-demo-page">
    <a-row :gutter="[16, 16]">
      <!-- 基础示例 -->
      <a-col :span="24">
        <HeatmapCalendar
          title="基础示例 - 年度生产热度"
          :data="basicData"
          :year="currentYear"
          @cell-click="handleCellClick"
        />
      </a-col>

      <!-- 自定义颜色示例 -->
      <a-col :span="24">
        <HeatmapCalendar
          title="自定义颜色 - 蓝色主题"
          :data="basicData"
          :year="currentYear"
          :color-levels="blueTheme"
          legend-less-text="不活跃"
          legend-more-text="非常活跃"
        />
      </a-col>

      <!-- 橙色主题示例 -->
      <a-col :span="24">
        <HeatmapCalendar
          title="橙色主题 - 设备运行时长"
          :data="randomData"
          :year="currentYear"
          :color-levels="orangeTheme"
        />
      </a-col>

      <!-- 自定义阈值示例 -->
      <a-col :span="24">
        <HeatmapCalendar
          title="自定义阈值 - 产蛋数量统计"
          :data="basicData"
          :year="currentYear"
          :level-thresholds="[50, 100, 150, 200]"
          :tooltip-formatter="customTooltipFormatter"
        />
      </a-col>

      <!-- 数据对比 -->
      <a-col :span="12">
        <HeatmapCalendar
          title="养殖场A - 产蛋热度"
          :data="farmAData"
          :year="currentYear"
          :bordered="true"
        />
      </a-col>
      <a-col :span="12">
        <HeatmapCalendar
          title="养殖场B - 产蛋热度"
          :data="farmBData"
          :year="currentYear"
          :bordered="true"
        />
      </a-col>

      <!-- 控制面板 -->
      <a-col :span="24">
        <a-card title="数据控制面板">
          <a-space>
            <a-button type="primary" @click="generateRandomData">
              生成随机数据
            </a-button>
            <a-button @click="clearData">
              清空数据
            </a-button>
            <a-select v-model="currentYear" style="width: 120px">
              <a-select-option :value="2023">2023年</a-select-option>
              <a-select-option :value="2024">2024年</a-select-option>
              <a-select-option :value="2025">2025年</a-select-option>
            </a-select>
          </a-space>

          <a-divider />

          <a-descriptions title="最近点击的单元格" :column="2">
            <a-descriptions-item label="日期">
              {{ lastClickedCell.date || '-' }}
            </a-descriptions-item>
            <a-descriptions-item label="数值">
              {{ lastClickedCell.value || '-' }}
            </a-descriptions-item>
            <a-descriptions-item label="标签">
              {{ lastClickedCell.label || '-' }}
            </a-descriptions-item>
          </a-descriptions>
        </a-card>
      </a-col>
    </a-row>
  </div>
</template>

<script>
import HeatmapCalendar from '@/components/Widgets/HeatmapCalendar.vue'

export default {
  name: 'HeatmapDemo',
  components: {
    HeatmapCalendar
  },
  data() {
    return {
      currentYear: new Date().getFullYear(),
      basicData: [],
      randomData: [],
      farmAData: [],
      farmBData: [],
      lastClickedCell: {},
      
      // 颜色主题
      blueTheme: ['#ebedf0', '#a8d5ff', '#5eb3ff', '#2e8bff', '#0052cc'],
      orangeTheme: ['#f0f0f0', '#ffd6a5', '#fdab3d', '#fd7f20', '#d14d00'],
      purpleTheme: ['#ebedf0', '#d5b3ff', '#b366ff', '#9933ff', '#7700cc']
    }
  },
  mounted() {
    this.generateRandomData()
  },
  methods: {
    // 生成随机数据
    generateRandomData() {
      this.basicData = this.generateYearData(this.currentYear, 0.7)
      this.randomData = this.generateYearData(this.currentYear, 0.6)
      this.farmAData = this.generateYearData(this.currentYear, 0.8)
      this.farmBData = this.generateYearData(this.currentYear, 0.75)
    },
    
    // 生成一年的模拟数据
    generateYearData(year, probability = 0.7) {
      const data = []
      const startDate = new Date(year, 0, 1)
      const endDate = new Date(year, 11, 31)
      
      let currentDate = new Date(startDate)
      while (currentDate <= endDate) {
        // 根据概率决定是否有数据
        if (Math.random() < probability) {
          const value = Math.floor(Math.random() * 250)
          const dateStr = this.formatDate(currentDate)
          
          data.push({
            date: dateStr,
            value: value,
            label: `产蛋 ${value} 枚`
          })
        }
        currentDate.setDate(currentDate.getDate() + 1)
      }
      
      return data
    },
    
    // 格式化日期
    formatDate(date) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    
    // 清空数据
    clearData() {
      this.basicData = []
      this.randomData = []
      this.farmAData = []
      this.farmBData = []
      this.lastClickedCell = {}
    },
    
    // 处理单元格点击
    handleCellClick(payload) {
      this.lastClickedCell = payload
      
      this.$message.success(`点击了 ${payload.date}，数值：${payload.value}`)
      
      console.log('单元格点击事件：', payload)
    },
    
    // 自定义 tooltip 格式化
    customTooltipFormatter(date, value, label) {
      if (value === 0) {
        return `${date}: 无生产记录`
      }
      
      let level = ''
      if (value <= 50) level = '低产'
      else if (value <= 100) level = '正常'
      else if (value <= 150) level = '高产'
      else if (value <= 200) level = '超高产'
      else level = '爆产'
      
      return `${date}: ${value} 枚 (${level})`
    }
  },
  watch: {
    currentYear() {
      this.generateRandomData()
    }
  }
}
</script>

<style scoped lang="scss">
.heatmap-demo-page {
  padding: 24px;
  background: #f0f2f5;
  min-height: 100vh;
}
</style>
